import { isDefined } from 'list-fns';
import React from 'react';
import LinkButton from '@sats-group/fresh-fitness-ui-lib/react/link-button';
import Message from '@sats-group/fresh-fitness-ui-lib/react/message';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import Location from '@sats-group/icons/32/location';
import CalendarClock from '@sats-group/icons/32/calendar-clock';

import ContentContainer from 'root/client/components/content-container/content-container';
import ClubLinkCard from 'root/client/components/club-link-card/club-link-card';
import Image from 'root/client/components/image/image';
import Layout from 'root/client/components/layout/layout';
import OpeningHoursTable from 'root/client/components/opening-hours-table/opening-hours-table';
import PersonalTrainerCards from 'root/client/components/profile-card-list/profile-card-list';
import ProductOptionCards from 'root/client/components/product-option-cards/product-option-cards';
import Slideshow from 'root/client/components/slideshow/slideshow';

import type { ClubPage as Props } from './club-page.types';
import Map from 'root/client/components/map/map';
import Carousel from 'root/client/components/carousel/carousel';

const ClubPage: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  address,
  closestClubsCarousel,
  closestClubsText,
  dropInButton,
  facilities,
  facilitiesTitle,
  equipment,
  equipmentTitle,
  information,
  layout,
  map,
  media = [],
  memberships,
  openingHours,
  personalTrainers,
  personalTrainersTitle,
  salesButton,
  slideshow,
  text,
  title,
}) => (
  <Layout {...layout}>
    <div className="club-page">
      {media.length > 0 ? (
        <div className="club-page__hero">
          <Slideshow {...slideshow}>
            {media.map(({ image, key }) => (
              <div className="club-page__slideshow-image">
                <Image
                  key={key}
                  {...image}
                  aspectRatio={Image.aspectRatios.Fill}
                />
              </div>
            ))}
          </Slideshow>
        </div>
      ) : null}
      <div className="club-page__main-content">
        <Text
          className="club-page__title"
          elementName="h1"
          size={Text.sizes.header2}
          variant={Text.variants.hero}
        >
          {title}
        </Text>
        <ContentContainer
          className="club-page__content"
          width={ContentContainer.widths.Large}
        >
          <div className="club-page__sales-actions">
            {salesButton ? (
              <LinkButton
                data-test-sales-flow
                theme={LinkButton.themes.cta}
                {...salesButton}
              />
            ) : null}
            {dropInButton ? (
              <LinkButton
                data-test-drop-in-trigger
                {...dropInButton}
                theme={LinkButton.themes.primaryWhite}
              />
            ) : null}
          </div>
        </ContentContainer>
        {information ? <Message {...information} type="info" /> : null}
        <ContentContainer
          className="club-page__content"
          width={ContentContainer.widths.Large}
        >
          <div className="club-page__split-section club-page__split-section--small-reverse">
            <div className="club-page__location">
              {map ? (
                <div className="club-page__map">
                  <Map {...map} />
                </div>
              ) : null}
              {address ? (
                <div className="club-page__address">
                  <Location />
                  <Text>{address}</Text>
                </div>
              ) : null}
            </div>
            <div className="club-page__opening-hours">
              <CalendarClock />
              <div className="club-page__opening-hours-section">
                {openingHours
                  .map(openingHour => (
                    <OpeningHoursTable
                      {...openingHour}
                      key={openingHour.title}
                    />
                  ))
                  .filter(isDefined)}
              </div>
            </div>
          </div>
          <div className="club-page__split-section">
            {text ? (
              <div className="club-page__description">
                <Text>{text}</Text>
              </div>
            ) : null}
            <div className="club-page__additionals">
              {facilities.length > 0 ? (
                <div className="club-page__facilities">
                  <Text
                    elementName="h2"
                    size={Text.sizes.header3}
                    theme={Text.themes.emphasis}
                  >
                    {facilitiesTitle}
                  </Text>
                  <ul>
                    {facilities.map(text => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {equipment.length > 0 ? (
                <div className="club-page__equipment">
                  <Text
                    elementName="h2"
                    size={Text.sizes.header3}
                    theme={Text.themes.emphasis}
                  >
                    {equipmentTitle}
                  </Text>

                  <ul>
                    {equipment.map(text => (
                      <li key={text}>{text}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </ContentContainer>
        {memberships ? (
          <ContentContainer className="club-page__memberships">
            <ProductOptionCards {...memberships} />
          </ContentContainer>
        ) : null}
        <div className="club-page__alternative-background">
          {closestClubsCarousel.entries.length ? (
            <ContentContainer
              className="club-page__nearby-clubs"
              width={ContentContainer.widths.Large}
            >
              <Text
                elementName="h2"
                size={Text.sizes.header3}
                variant={Text.variants.hero}
              >
                {closestClubsText.title}
              </Text>
              <Text>{closestClubsText.description}</Text>
              <Carousel
                {...closestClubsCarousel}
                entryRenderer={ClubLinkCard}
              />
            </ContentContainer>
          ) : null}
        </div>
        <ContentContainer width={ContentContainer.widths.Large}>
          {personalTrainers && personalTrainers.cards.length ? (
            <div className="club-page__pts">
              <Text
                elementName="h2"
                size={Text.sizes.header3}
                variant={Text.variants.hero}
              >
                {personalTrainersTitle}
              </Text>
              <PersonalTrainerCards {...personalTrainers} slider={true} />
            </div>
          ) : null}
        </ContentContainer>
      </div>
    </div>
  </Layout>
);

export default ClubPage;
